import { Component, inject, OnInit } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { Gas, LocalizeGasNamePipe } from "@domain/gas/gas";
import { GasService } from "@domain/gas/gas.service";
import { ProductService } from "@domain/product/product.service";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { AreaHeaderComponent } from "@odx/angular/components/area-header";
import { ButtonComponent } from "@odx/angular/components/button";
import { ModalContentDirective, ModalRef } from "@odx/angular/components/modal";
import { TableCellDirective, TableComponent, TableHeaderCell, TableRowDirective } from "@odx/angular/components/table";
import { ProductItem } from "@project/product-item";
import { forkJoin, map, Observable, of } from "rxjs";

export type ModalProductItem = ProductItem & { detectionRange?: number };
type FloorplanItemDetailsModalData = {
  title?: string;
  gasId?: string;
  productItems: ModalProductItem[];
  attachments: ModalProductItem[];
  customProducts: PlaceholderProduct[];
};

interface TableData {
  quantity: number;
  productName: string;
  productId: string;
  detectionRange?: number;
}

@Component({
  selector: "app-floorplan-item-details-modal",
  imports: [
    AreaHeaderComponent,
    ButtonComponent,
    ModalContentDirective,
    UiKitModule,
    TableRowDirective,
    TableCellDirective,
    TableComponent,
  ],
  providers: [LocalizeGasNamePipe],
  templateUrl: "./floorplan-item-details-modal.component.html",
  styleUrl: "./floorplan-item-details-modal.component.scss",
  standalone: true,
})
export class FloorplanItemDetailsModal implements OnInit {
  public readonly modalRef = inject(ModalRef<FloorplanItemDetailsModalData>);
  private productService = inject(ProductService);
  private gasService = inject(GasService);
  private localizeGasNamePipe = inject(LocalizeGasNamePipe);

  protected products$!: Observable<TableData[]>;
  protected subtitle$!: Observable<string>;
  protected hasDetectionRange = false;
  protected headerData: TableHeaderCell[] = [];
  protected modalTitle = $localize`:@@group.modal.attachments.header:Konfiguration`;

  private readonly headerConfig: (TableHeaderCell & { isVisible?: () => boolean })[] = [
    { name: "quantity", title: $localize`:@@attachmentsTable.column0.header:Anzahl` },
    { name: "productName", title: $localize`:@@attachmentsTable.column1.header:Bezeichnung` },
    { name: "productId", title: $localize`:@@attachmentsTable.column2.header:Sachnummer` },
    {
      name: "detectionRange",
      title: $localize`:@@attachmentsTable.column3.header:Durchmesser Überwachungsbereich`,
      isVisible: () => this.hasDetectionRange,
    },
  ];

  ngOnInit() {
    const productItems: ModalProductItem[] = this.modalRef.data.productItems;
    this.modalTitle = this.modalRef.data.title || this.modalTitle;
    this.subtitle$ = this.resolveSubtitle();
    this.hasDetectionRange = productItems.some((item) => item.detectionRange !== undefined);
    this.headerData = this.headerConfig
      .filter((headerCell) => headerCell.isVisible?.() ?? true)
      .map((headerCell) => ({ name: headerCell.name, title: headerCell.title }));
    this.products$ = this.initTableData();
  }

  onConfirm() {
    this.modalRef.close(true);
  }

  private initTableData(): Observable<TableData[]> {
    const customProducts = this.mapCustomProducts();
    const productItems: ModalProductItem[] = this.modalRef.data.productItems;
    const attachments: ModalProductItem[] = this.modalRef.data.attachments;

    const fetchedProducts$ =
      productItems.length > 0
        ? forkJoin(
            productItems.map((productItem) =>
              this.productService.getProductNameById(productItem.productId).pipe(
                map((name) => ({
                  quantity: productItem.quantity,
                  productId: productItem.productId,
                  productName: name || "",
                  detectionRange: productItem.detectionRange,
                })),
              ),
            ),
          )
        : of([] as TableData[]);
    const fetchedAttachments$ =
      attachments.length > 0
        ? forkJoin(
            attachments.map((attachment) =>
              this.productService.getProductNameById(attachment.productId).pipe(
                map((name) => ({
                  quantity: attachment.quantity,
                  productId: attachment.productId,
                  productName: name || "",
                })),
              ),
            ),
          )
        : of([] as TableData[]);

    return forkJoin([fetchedProducts$, fetchedAttachments$]).pipe(
      map(([products, attachments]) => [...products, ...attachments, ...customProducts]),
    );
  }

  private resolveSubtitle(): Observable<string> {
    const gasId = this.modalRef.data.gasId;
    if (!gasId) {
      return of("");
    }

    return this.gasService.getGasById(gasId).pipe(
      map((gas: Gas | undefined) => {
        if (!gas) {
          return "";
        }
        const gasName = this.localizeGasNamePipe.transform(gas);
        const rawCasNumber = gas.definition.casNumber;
        if (!rawCasNumber || gasName === rawCasNumber) {
          return gasName;
        }

        const casNumber = rawCasNumber.startsWith("CAS") ? rawCasNumber.substring(3).trim() : rawCasNumber;
        if (!casNumber || gasName === casNumber) {
          return gasName;
        }

        const formattedCasNumber = $localize`:@@floorplan.configuredProduct.substance.casNumber:CAS-Nr.: ${casNumber}`;
        return gasName ? `${gasName}, ${formattedCasNumber}` : formattedCasNumber;
      }),
    );
  }

  private mapCustomProducts(): TableData[] {
    return this.modalRef.data.customProducts.map((product: PlaceholderProduct) => ({
      quantity: 1,
      productId: product.id,
      productName: product.name || $localize`:@@customPartNumber.placeholderName:Benutzerdefinierte Sachnummer`,
    }));
  }
}
